import { Button, Card, Col, Divider, Flex, Row, Typography } from 'antd';
import { CheckSquareOutlined } from '@ant-design/icons';
import { ALL_QUESTIONS, StepIndex } from '../../../constants/question';
import OhatCheckHeader from '../../components/check/OhatCheckHeader';
import OhatResultPanel from '../../components/check/OhatResultPanel';
import OhatPhotoSection from '../../components/check/OhatPhotoSection';
import { createRef, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ohatScoreFindFirstAndLatest,
  patientFindOne,
  useOhatScoreCreate,
} from '../../../libs/ohat-api';
import useMobile from '../../../hooks/useMobile';
import { Patient } from '@models/patient';
import OhatCheckQuestionRow from '../../components/check/OhatCheckQuestionRow';
import { useGAEvent } from '../../../hooks/useGAEvent';
import { OhatScore } from '@models/ohatScore';
import { getInitAnswers } from '../../../utils/ohat-score-utils';
import { getNowISO } from '../../../utils/date';
import { usePlan } from '../../../hooks/usePlan';
import { useTranslation } from 'react-i18next';

export type Props = {
  isGuestMode: boolean;
};

export default function CheckScorePage(props: Props) {
  const { isGuestMode } = props;
  const navigate = useNavigate();
  const { sendClickEvent } = useGAEvent();
  const param = useParams();
  const { isMobile } = useMobile();
  const { t } = useTranslation();
  const [patient, setPatient] = useState<Patient>();
  const [oldOhatScores, setOldOhatScores] = useState<OhatScore[]>([]);
  const [step, setStep] = useState<StepIndex>(0);
  const questionRefs = useRef(
    ALL_QUESTIONS.map(() => createRef<HTMLDivElement>()),
  );
  // 項目別メモ欄（ライトプラン）のスクロール先
  const memoRefs = useRef(ALL_QUESTIONS.map(() => createRef<HTMLDivElement>()));
  const [answers, setAnswers] = useState(getInitAnswers());
  const [isComplete, setIsComplete] = useState(false);
  // 評価保存後に採番される評価ID（写真を紐づけるために保持）
  const [createdScoreId, setCreatedScoreId] = useState<string>();
  const { mutateAsync: createOhatScore } = useOhatScoreCreate();
  const { canUse } = usePlan();
  // 項目別メモはライトプラン限定。ゲストモードでは常に非表示。
  const canUseItemMemo = !isGuestMode && canUse('item_memo');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [isComplete]);

  useEffect(() => {
    const fetchPatient = async () => {
      if (!isGuestMode && param.id) {
        const patientData = await patientFindOne(param.id);
        setPatient(patientData);
        const ohatScoreData = await ohatScoreFindFirstAndLatest(param.id, {
          baseDate: getNowISO(),
        });
        setOldOhatScores(ohatScoreData);
      }
    };
    fetchPatient();
  }, [isGuestMode, param.id]);

  const onBack = () => {
    if (step > 0) {
      setStep((step - 1) as StepIndex);
    } else {
      isGuestMode
        ? (window.location.href = `https://ohat.jp`)
        : navigate(`/patients/${param.id}`);
    }
  };

  const onNext = async () => {
    sendClickEvent('OHAT結果確認');
    setIsComplete(true);
    if (!isGuestMode) {
      // 空文字は送らず、入力があった項目のみメモを付与する（サーバー側でitem_memoゲート判定）。
      const memoAt = (i: number) =>
        canUseItemMemo ? answers.memos?.[i]?.trim() || undefined : undefined;
      const created = await createOhatScore({
        data: {
          patient: { connect: { id: param.id! } },
          q1: answers.score[0],
          q2: answers.score[1],
          q3: answers.score[2],
          q4: answers.score[3],
          q5: answers.score[4],
          q6: answers.score[5],
          q7: answers.score[6],
          q8: answers.score[7],
          q1Memo: memoAt(0),
          q2Memo: memoAt(1),
          q3Memo: memoAt(2),
          q4Memo: memoAt(3),
          q5Memo: memoAt(4),
          q6Memo: memoAt(5),
          q7Memo: memoAt(6),
          q8Memo: memoAt(7),
        },
      });
      setCreatedScoreId(created.id);
      // 評価者はサーバ側で認証情報から設定されるため、保存結果から表示名を受け取る
      setAnswers((prev) => ({
        ...prev,
        evaluatorName: created.evaluatorName ?? undefined,
      }));
    }
  };

  const onSelect = (step: StepIndex, answerIndex: number) => {
    const newState = Object.assign({}, answers);
    newState.score[step] = answerIndex;
    setAnswers(newState);

    const offset = 60; // 例: ヘッダーの高さなど
    const memoBox = memoRefs.current[step]?.current;
    if (canUseItemMemo && memoBox) {
      // メモ欄が表示される場合は、同じ項目のメモ欄へスクロール（自動フォーカスはしない）
      window.scrollTo({
        top: memoBox.getBoundingClientRect().top + window.scrollY - offset,
        behavior: 'smooth',
      });
    } else if (step < ALL_QUESTIONS.length - 1) {
      const element = questionRefs.current[step + 1].current!;
      const offsetTop = element.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: offsetTop - offset, // オフセットを引く
        behavior: 'smooth',
      });
    } else {
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: 'smooth',
      });
    }
  };

  const onMemoChange = (step: StepIndex, value: string) => {
    setAnswers((prev) => ({
      ...prev,
      memos: { ...(prev.memos ?? {}), [step]: value },
    }));
  };

  return (
    <Row gutter={[16, 8]} style={{ flex: 1, justifyContent: 'center' }}>
      <Col
        // 結果表示中は結果パネルを最大1200pxまで広げて左右の余白を減らす。
        // 設問入力中は読みやすさを保つため従来の幅のままにする。
        {...(isComplete
          ? { span: 24, style: { maxWidth: 1200 } }
          : { lg: 14, md: 18, sm: 20, xs: 24 })}
      >
        <Flex vertical gap={20}>
          {isGuestMode && (
            <img
              src={`${process.env.PUBLIC_URL}/logo.svg`}
              alt="Logo"
              height={50}
              style={{ marginTop: 20 }}
            />
          )}
          <Card
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            {isComplete ? (
              <OhatResultPanel
                answers={answers}
                isGuestMode={isGuestMode}
                oldOhatScores={oldOhatScores}
                patient={patient}
              />
            ) : (
              <>
                <OhatCheckHeader onBack={onBack} />
                <Divider />
                <Typography.Text style={{ fontSize: isMobile() ? 14 : 16 }}>
                  {t('ohat.instruction')}
                </Typography.Text>

                {ALL_QUESTIONS.map((v, i) => {
                  const step = i as StepIndex;
                  return (
                    <OhatCheckQuestionRow
                      innerRef={questionRefs.current[step]}
                      memoRef={memoRefs.current[step]}
                      onSelect={onSelect}
                      question={v}
                      answer={answers.score[step]}
                      step={step}
                      showMemo={canUseItemMemo}
                      memo={answers.memos?.[step] ?? ''}
                      onMemoChange={onMemoChange}
                      key={step}
                    />
                  );
                })}
                <Flex justify="center" align="center" vertical>
                  <Typography.Text style={{ fontSize: isMobile() ? 9 : 12 }}>
                    {t('ohat.attribution')}
                  </Typography.Text>
                  <Button
                    type="primary"
                    size="large"
                    shape="round"
                    icon={<CheckSquareOutlined />}
                    style={
                      isMobile()
                        ? { marginTop: 20, width: '100%' }
                        : { marginTop: 20, width: '50%' }
                    }
                    disabled={
                      !!Object.values(answers.score).find((v) => v === -1)
                    }
                    onClick={onNext}
                  >
                    {t('ohat.seeResult')}
                  </Button>
                </Flex>
              </>
            )}
          </Card>
          {isComplete && !isGuestMode && createdScoreId && (
            <OhatPhotoSection ohatScoreId={createdScoreId} />
          )}
        </Flex>
      </Col>
    </Row>
  );
}
